import type { CardDto, ColumnDto } from '@kanban/shared';
import { positionBetween } from '@kanban/shared';

/**
 * Zielposition beim Ablegen einer Karte.
 *
 * Der Client rechnet den Fractional Key selbst, weil er die Nachbarn aus dem
 * gerenderten Board ohnehin kennt (SPEC 5.1). Er schickt denselben Key an
 * `POST /cards/:id/move`, den er optimistisch anzeigt — dadurch rutscht beim
 * anschliessenden Invalidate nichts nach.
 *
 * Der Fallstrick steckt im Verschieben innerhalb derselben Spalte: die
 * Nachbarn muessen aus der Liste OHNE die gezogene Karte bestimmt werden,
 * sonst ist die gezogene Karte ihr eigener Nachbar und der erzeugte Key liegt
 * auf der falschen Seite.
 */
export interface DropTarget {
  columnId: string;
  /** Index in der Zielspalte, gezaehlt ohne die gezogene Karte. */
  index: number;
}

/**
 * Id der Papierkorb-Zone.
 *
 * Reserviert und mit Unterstrichen gerahmt, damit sie sich nie mit einer
 * Spalten- oder Karten-Id ueberschneiden kann — beide sind cuids.
 */
export const TRASH_DROPPABLE_ID = '__papierkorb__';

/**
 * Was ein Ablegen bedeutet.
 *
 * Bewusst eine Union und kein `DropTarget | null` mit Sonderfall: Verschieben
 * und Archivieren sind zwei verschiedene Dinge, und ein Papierkorb, der sich
 * als Spalte mit dem Index -1 tarnt, laedt genau die Verwechslung ein, die
 * hier Karten kostet.
 */
export type DropAction = { kind: 'move'; target: DropTarget } | { kind: 'trash' };

export function positionForDrop(
  columns: readonly ColumnDto[],
  cardId: string,
  target: DropTarget,
): string {
  const column = columns.find((c) => c.id === target.columnId);
  if (!column) throw new Error(`Zielspalte ${target.columnId} existiert nicht`);

  const others = column.cards.filter((card) => card.id !== cardId);
  const index = Math.max(0, Math.min(target.index, others.length));

  const prev = index > 0 ? (others[index - 1]?.position ?? null) : null;
  const next = index < others.length ? (others[index]?.position ?? null) : null;

  return positionBetween(prev, next);
}

/**
 * Board-Zustand nach dem Ablegen — Grundlage des optimistischen Updates.
 * Reine Funktion, damit sie ohne React und ohne DOM testbar bleibt.
 */
export function applyDrop(
  columns: readonly ColumnDto[],
  cardId: string,
  target: DropTarget,
  position: string,
): ColumnDto[] {
  const moved = columns.flatMap((column) => column.cards).find((card) => card.id === cardId);
  if (!moved) return [...columns];

  const targetColumn = columns.find((column) => column.id === target.columnId);
  const nowCompleted = targetColumn?.isDoneColumn
    ? (moved.completedAt ?? new Date().toISOString())
    : null;

  const updated: CardDto = {
    ...moved,
    columnId: target.columnId,
    position,
    completedAt: nowCompleted,
  };

  return columns.map((column) => {
    const without = column.cards.filter((card) => card.id !== cardId);
    if (column.id !== target.columnId) return { ...column, cards: without };
    // Nach position sortieren statt an den Index zu spleissen: der Key ist die
    // Wahrheit, und genau danach sortiert auch der Server.
    const cards = [...without, updated].sort((a, b) =>
      a.position < b.position ? -1 : a.position > b.position ? 1 : 0,
    );
    return { ...column, cards };
  });
}

/**
 * Bedeutung eines Ablegens aus der Kollisionserkennung von dnd-kit ableiten.
 *
 * `overId` ist die Papierkorb-Zone, eine Karte (dann wird davor oder dahinter
 * eingefuegt) oder eine leere Spalte (dann ans Ende).
 */
export function dropActionFromOver(
  columns: readonly ColumnDto[],
  activeCardId: string,
  overId: string,
  /** Zeigt der Zeiger auf die untere Haelfte der Zielkarte? */
  below: boolean,
): DropAction | null {
  if (overId === TRASH_DROPPABLE_ID) return { kind: 'trash' };

  const overColumn = columns.find((column) => column.id === overId);
  if (overColumn) {
    // Direkt ueber der Spalte (leerer Bereich) -> ans Ende.
    const others = overColumn.cards.filter((card) => card.id !== activeCardId);
    return { kind: 'move', target: { columnId: overColumn.id, index: others.length } };
  }

  const column = columns.find((c) => c.cards.some((card) => card.id === overId));
  if (!column) return null;

  const others = column.cards.filter((card) => card.id !== activeCardId);
  const overIndex = others.findIndex((card) => card.id === overId);
  const index = overIndex === -1 ? others.length : below ? overIndex + 1 : overIndex;

  return { kind: 'move', target: { columnId: column.id, index } };
}
